import { useEffect, useRef, useState } from 'react';

const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
const RENDER_SCALE = 1.5;
// Stesse dimensioni del box firma del provider (100x42 pt, vedi api_firme.ts)
const MARKER_W = 100 * RENDER_SCALE; // px sul canvas
const MARKER_H = 42 * RENDER_SCALE; // px sul canvas

let pdfjsPromise = null;
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PDFJS_URL;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => {
        pdfjsPromise = null;
        reject(new Error('Impossibile caricare il visualizzatore PDF'));
      };
      document.head.appendChild(script);
    });
  }
  return pdfjsPromise;
};

// Punto cliccato (px CSS relativi al canvas) -> punti PDF della pagina, origine in alto a sinistra (coordinate di visualizzazione).
// La conversione verso la convenzione del provider è in toProviderSignPosition (api_firme.ts).
// Il canvas può essere ridimensionato via CSS: si passa dalla dimensione visualizzata
// a quella della pagina in punti (canvas.width / RENDER_SCALE), valida per qualsiasi formato.
export const clickToPdfPoint = (clickX, clickY, displayedW, displayedH, pageWpt, pageHpt) => ({
  x: Math.round((clickX / displayedW) * pageWpt),
  y: Math.round((clickY / displayedH) * pageHpt),
});

export const pdfPointToPercent = (pos, pageWpt, pageHpt) => ({
  left: `${(pos.x / pageWpt) * 100}%`,
  top: `${(pos.y / pageHpt) * 100}%`,
});

export default function FirmePdfSignaturePlacer({ file, signers, activeKey, positions, onPlace, onActivate }) {
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState(null);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const dragKeyRef = useRef(null);
  const docRef = useRef(null); // ultimo PDF caricato, da liberare quando viene sostituito

  useEffect(() => {
    let cancelled = false;
    setPdf(null);
    setPage(1);
    setNumPages(0);
    setPageSize(null);
    setError(null);
    if (!file) return undefined;

    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const data = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled) {
          doc.destroy();
          return;
        }
        // Il PDF precedente non è più in rendering: lo stato è stato azzerato all'avvio di questo effetto
        docRef.current?.destroy();
        docRef.current = doc;
        setPdf(doc);
        setNumPages(doc.numPages);
      } catch (err) {
        if (!cancelled) setError('Impossibile leggere il PDF selezionato');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    if (!pdf) return undefined;
    let cancelled = false;
    setPageSize(null);

    (async () => {
      try {
        const p = await pdf.getPage(page);
        if (cancelled) return;
        const viewport = p.getViewport({ scale: RENDER_SCALE });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        setPageSize({ w: viewport.width / RENDER_SCALE, h: viewport.height / RENDER_SCALE });

        if (renderTaskRef.current) renderTaskRef.current.cancel();
        const task = p.render({ canvasContext: canvas.getContext('2d'), viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException' && !cancelled) {
          setError('Impossibile leggere il PDF selezionato');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdf, page]);

  // Libera l'ultimo PDF quando il componente viene smontato
  useEffect(() => () => {
    docRef.current?.destroy();
    docRef.current = null;
  }, []);

  // Coordinate del puntatore -> punti PDF, limitate all'area della pagina
  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
    const cy = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
    return clickToPdfPoint(cx, cy, rect.width, rect.height, pageSize.w, pageSize.h);
  };

  const handleClick = (e) => {
    if (!activeKey || !pageSize) return;
    // index null: aggiunge una nuova posizione
    onPlace(activeKey, { pag: page, ...pointFromEvent(e), pageH: pageSize.h }, null);
  };

  // Trascinamento di una specifica firma già posizionata
  const startDrag = (e, key, index) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragKeyRef.current = { key, index };
    onActivate?.(key);
  };

  const moveDrag = (e, key, index) => {
    if (
      !dragKeyRef.current ||
      dragKeyRef.current.key !== key ||
      dragKeyRef.current.index !== index ||
      !pageSize
    ) return;
    // Tasto rilasciato senza un pointerup (menu contestuale, gesture di sistema): il trascinamento finisce
    if (!(e.buttons & 1)) {
      dragKeyRef.current = null;
      return;
    }

    // index valorizzato: aggiorna soltanto la posizione trascinata
    onPlace(key, { pag: page, ...pointFromEvent(e), pageH: pageSize.h }, index);
  };

  const endDrag = () => {
    dragKeyRef.current = null;
  };

  if (!file) return null;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const activeSigner = signers.find((s) => s.key === activeKey);
  const totalPositions = Object.values(positions).reduce(
    (total, items) => total + (Array.isArray(items) ? items.length : 0),
    0
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
        <button
          type="button"
          onClick={() => setPage((p) => p - 1)}
          disabled={page <= 1}
          className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
        >
          &lt;
        </button>
        <span>Pagina {page} di {numPages}</span>
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= numPages}
          className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
        >
          &gt;
        </button>
        {activeSigner && (
          <span className="text-indigo-700">
            Clicca sul documento per aggiungere una firma di {activeSigner.label}. Trascina una firma già posizionata per spostarla.
          </span>
        )}
      </div>

      <div className="text-xs text-gray-500">
        Posizioni di firma inserite: {totalPositions}
      </div>

      <div className="relative inline-block max-w-full">
        <canvas
          ref={canvasRef}
          className="block max-w-full h-auto border border-gray-300 cursor-crosshair"
          onClick={handleClick}
        />

        {pageSize && signers.flatMap((signer) => {
          const signerPositions = positions[signer.key] || [];
          const isActive = signer.key === activeKey;

          return signerPositions.map((pos, index) => {
            if (pos.pag !== page) return null;

            return (
              <div
                key={`${signer.key}-${index}`}
                onPointerDown={(e) => startDrag(e, signer.key, index)}
                onPointerMove={(e) => moveDrag(e, signer.key, index)}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onLostPointerCapture={endDrag}
                onClick={(e) => e.stopPropagation()}
                title="Trascina per spostare la firma"
                className={`absolute cursor-move select-none touch-none border-2 text-xs font-medium px-1 flex items-center justify-center text-center ${
                  isActive
                    ? 'border-teal-600 bg-teal-100/70 text-indigo-900'
                    : 'border-indigo-600 bg-indigo-100/70 text-indigo-900'
                }`}
                style={{
                  ...pdfPointToPercent(pos, pageSize.w, pageSize.h),
                  width: `${(MARKER_W / RENDER_SCALE / pageSize.w) * 100}%`,
                  height: `${(MARKER_H / RENDER_SCALE / pageSize.h) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <span className="truncate">
                  {signer.label} ({index + 1})
                </span>
              </div>
            );
          });
        })}
      </div>
    </div>
  );
}
